"use client";

import React from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Stagger, StaggerItem } from "@/components/shared/motion";
import { ShoppingCart, QrCode, Activity, Globe2, Zap, EyeOff, Signal, Smartphone, ArrowRight } from "lucide-react";

/**
 * Re-digital — home 3-column band per generated_homepage.png:
 * (1) "Get online in 3 simple steps" · (2) "Premium. Simple. Reliable." benefit
 * grid · (3) app teaser. NO-FAKE-APP: instead of working App Store / Google Play
 * download badges (no app exists yet), this frames the dashboard as "app coming
 * soon — your account dashboard, ready" pointing to the real account/order flow.
 */
export default function HomeBand() {
  const { t } = useTranslations();

  const steps = [
    { n: "01", icon: ShoppingCart, title: t("dp.band.s1", "Choose a plan"), body: t("dp.band.s1b", "Pick a destination and select your data plan.") },
    { n: "02", icon: QrCode, title: t("dp.band.s2", "Install eSIM"), body: t("dp.band.s2b", "Scan the QR code or install with one tap.") },
    { n: "03", icon: Activity, title: t("dp.band.s3", "Connect instantly"), body: t("dp.band.s3b", "Activate and enjoy high-speed data.") },
  ];

  const benefits = [
    { icon: Globe2, title: t("dp.band.b1", "200+ destinations"), body: t("dp.band.b1b", "Global coverage you can trust.") },
    { icon: EyeOff, title: t("dp.band.b2", "No hidden fees"), body: t("dp.band.b2b", "What you see is what you pay.") },
    { icon: Zap, title: t("dp.band.b3", "Instant delivery"), body: t("dp.band.b3b", "eSIM in seconds, 24/7.") },
    { icon: Signal, title: t("dp.band.b4", "Top networks"), body: t("dp.band.b4b", "Connect to the best local carriers.") },
  ];

  return (
    <Stagger as="section" className="container mx-auto px-4 py-8 grid lg:grid-cols-3 gap-4" staggerChildren={0.1} amount={0.15}>
      {/* steps */}
      <StaggerItem as="div" y={20} className="dp-card p-6">
        <h2 className="text-xl text-foreground mb-5">{t("dp.band.stepsTitle", "Get online in 3 simple steps")}</h2>
        <div className="space-y-4">
          {steps.map(({ n, icon: Icon, title, body }) => (
            <div key={n} className="flex items-start gap-3">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl dp-chip text-primary shrink-0"><Icon className="w-5 h-5" /></span>
              <div><div className="text-xs font-bold text-primary">{n}</div><div className="font-bold text-foreground text-sm">{title}</div><div className="text-xs text-muted-foreground">{body}</div></div>
            </div>
          ))}
        </div>
      </StaggerItem>

      {/* benefits */}
      <StaggerItem as="div" y={20} className="dp-card p-6">
        <h2 className="text-xl text-foreground mb-5">{t("dp.band.benefitsTitle", "Premium. Simple. Reliable.")}</h2>
        <div className="grid grid-cols-2 gap-4">
          {benefits.map(({ icon: Icon, title, body }) => (
            <div key={title}>
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg dp-chip text-primary mb-2"><Icon className="w-4 h-4" /></span>
              <div className="font-bold text-foreground text-sm">{title}</div>
              <div className="text-xs text-muted-foreground">{body}</div>
            </div>
          ))}
        </div>
      </StaggerItem>

      {/* app teaser (no fake app) */}
      <StaggerItem as="div" y={20} className="dp-card dp-glow p-6 flex flex-col">
        <span className="inline-flex items-center gap-2 text-xs font-bold text-primary uppercase tracking-wide mb-2"><Smartphone className="w-4 h-4" /> {t("dp.band.appEyebrow", "App coming soon")}</span>
        <h2 className="text-xl text-foreground">{t("dp.band.appTitle", "Your dashboard, in your pocket.")}</h2>
        <p className="mt-2 text-sm text-muted-foreground flex-1">{t("dp.band.appBody", "Track data, manage plans and top up from one account. The mobile app is on the way — manage everything from your account dashboard today.")}</p>
        <Link href="/orders/" onClick={() => { try { track({ event_name: "app_interest_clicked", event_id: `app_interest_clicked:app_intent_waitlist_v1:${Date.now()}`, experiment_id: "app_intent_waitlist_v1" }); } catch {} }} className="mt-4 dp-pill inline-flex items-center justify-center gap-2 h-11 rounded-full font-bold transition-all active:scale-[0.98]">
          {t("dp.band.appCta", "Open my dashboard")} <ArrowRight className="w-4 h-4" />
        </Link>
        <p className="mt-2 text-[11px] text-muted-foreground text-center">{t("dp.band.appNote", "Manage orders and top-ups online — no app required.")}</p>
      </StaggerItem>
    </Stagger>
  );
}
